/**
 * @fileoverview `<StatusBadge>` — badges de statuts métier GNPC.
 *
 * Centralise la couleur et le libellé des statuts financiers, STI,
 * intégration, remboursement et export. Les écrans ne doivent pas recréer
 * leur propre mapping enum -> couleur : les statuts portent des conséquences
 * financières et doivent rester cohérents partout.
 *
 * @see Documents/openapi.yaml — schemas PaymentStatus, SgiStatus, ExportJob
 */

import { cn } from '../lib/utils.ts';

export type PaymentStatus = 'INITIATED' | 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
export type SgiStatus = 'NOT_APPLICABLE' | 'PENDING' | 'PARTIAL' | 'RECORDED' | 'FAILED';
export type IntegrationStatus = 'UP' | 'DOWN' | 'DEGRADED';
export type RefundStatus = 'REQUESTED' | 'APPROVED' | 'EXECUTED' | 'REJECTED';
export type ExportStatus = 'PENDING' | 'COMPLETED' | 'FAILED';

export interface StatusBadgeProps {
  /** Famille de statut à afficher. */
  kind: 'payment' | 'sgi' | 'integration' | 'refund' | 'export';
  /** Valeur enum issue du contrat API ou du DDL. */
  status: PaymentStatus | SgiStatus | IntegrationStatus | RefundStatus | ExportStatus;
  /** Affiche un libellé plus court pour les tableaux mobiles. */
  compact?: boolean;
  className?: string;
}

const LABELS: Record<string, string> = {
  INITIATED: 'Initié',
  PENDING: 'En cours',
  PAID: 'Payé',
  FAILED: 'Échec',
  EXPIRED: 'Expiré',
  NOT_APPLICABLE: 'Non applicable',
  PARTIAL: 'Partiel',
  RECORDED: 'Enregistré Quipux',
  UP: 'Opérationnel',
  DOWN: 'Indisponible',
  DEGRADED: 'Dégradé',
  REQUESTED: 'Demandé',
  APPROVED: 'Approuvé',
  EXECUTED: 'Exécuté',
  REJECTED: 'Rejeté',
  COMPLETED: 'Terminé',
};

const COMPACT_LABELS: Record<string, string> = {
  NOT_APPLICABLE: 'N/A',
  RECORDED: 'Quipux OK',
  COMPLETED: 'OK',
};

/**
 * Mapping statut → classes couleur.
 *
 * Contraste WCAG 2.1 AA (axe color-contrast) : le TEXTE utilise la nuance
 * 800 du statut (`--color-status-*-800`, ratios ≥ 4,5:1 calculés dans
 * `globals.css`), jamais la couleur de base — celle-ci ne donne que
 * 2,2–3,8:1 sur les fonds teintés `/15`. La couleur de base reste
 * réservée au FOND (`/15`) et à la bordure (`/25`).
 */
const COLORS: Record<string, string> = {
  INITIATED: 'bg-status-initiated/15 text-quipux-blue-800 border-status-initiated/25',
  PENDING: 'bg-status-pending/15 text-status-pending-800 border-status-pending/25',
  PAID: 'bg-status-paid/15 text-status-paid-800 border-status-paid/25',
  FAILED: 'bg-status-failed/15 text-status-failed-800 border-status-failed/25',
  EXPIRED: 'bg-status-expired/25 text-quipux-navy-800 border-status-expired/40',
  NOT_APPLICABLE: 'bg-status-not-applicable/30 text-quipux-navy-800 border-status-not-applicable',
  PARTIAL: 'bg-status-partial/15 text-status-partial-800 border-status-partial/25',
  RECORDED: 'bg-status-recorded/15 text-status-recorded-800 border-status-recorded/25',
  UP: 'bg-integration-up/15 text-integration-up-800 border-integration-up/25',
  DOWN: 'bg-integration-down/15 text-integration-down-800 border-integration-down/25',
  DEGRADED: 'bg-integration-degraded/15 text-integration-degraded-800 border-integration-degraded/25',
  REQUESTED: 'bg-status-pending/15 text-status-pending-800 border-status-pending/25',
  APPROVED: 'bg-quipux-blue-50 text-quipux-blue-800 border-quipux-blue-200',
  EXECUTED: 'bg-status-paid/15 text-status-paid-800 border-status-paid/25',
  REJECTED: 'bg-status-failed/15 text-status-failed-800 border-status-failed/25',
  COMPLETED: 'bg-status-paid/15 text-status-paid-800 border-status-paid/25',
};

/**
 * Badge de statut métier.
 *
 * @param props - Famille, statut, mode compact et classe CSS.
 * @returns Badge arrondi avec couleur et libellé centralisés.
 *
 * @example
 * ```tsx
 * <StatusBadge kind="payment" status="PAID" />
 * <StatusBadge kind="sgi" status="FAILED" compact />
 * ```
 */
export function StatusBadge({
  kind,
  status,
  compact = false,
  className,
}: StatusBadgeProps): React.JSX.Element {
  const label = compact
    ? (COMPACT_LABELS[status] ?? LABELS[status] ?? status)
    : (LABELS[status] ?? status);

  return (
    <span
      data-status-kind={kind}
      data-status={status}
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium',
        COLORS[status],
        className,
      )}
    >
      {label}
    </span>
  );
}
